import { createAsyncThunk } from "@reduxjs/toolkit";
import ProjectServices from "../../services/projectServices";

/**
 * Fetch notifications with pagination and optional category filter
 * @param {Object} params
 * @param {string} params.category - Optional category filter (decision, task, assurance, info, celebration)
 * @param {number} params.pageSize - Items per page (default 20)
 * @param {number} params.cursor - Cursor for pagination (ID of last item)
 * @param {boolean} params.append - If true, append to existing notifications (for infinite scroll)
 */
export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const userId = auth.userInfo?.user_id;

      const response = await ProjectServices.getNotifications({
        userId,
        category: params.category,
        pageSize: params.pageSize || 20,
        cursor: params.cursor,
        isRead: params.isRead,
      });

      return {
        ...response.data,
        append: params.append || false,
        category: params.category || null,
      };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch notifications";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Fetch all notifications for the current user (legacy - no pagination)
 */
export const fetchAllNotifications = createAsyncThunk(
  "notifications/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const userId = auth.userInfo?.user_id;
      const response = await ProjectServices.getUsersNotification(userId);
      // Handle both paginated and legacy response formats
      const data = response.data;
      if (data.results) {
        return data.results; // Paginated response
      }
      return data; // Legacy response (array)
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch notifications";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Fetch unread notification preview for navbar
 * Returns lightweight data with unread count
 */
export const fetchUnreadPreview = createAsyncThunk(
  "notifications/fetchUnreadPreview",
  async ({ limit = 5 } = {}, { rejectWithValue }) => {
    try {
      const response =
        await ProjectServices.getUnreadNotificationPreview(limit);
      return response.data;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to fetch unread preview";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Mark a single notification as read
 */
export const markNotificationRead = createAsyncThunk(
  "notifications/markRead",
  async (notificationId, { rejectWithValue }) => {
    try {
      const response =
        await ProjectServices.markNotificationRead(notificationId);
      return { id: notificationId, ...response.data };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to mark notification as read";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Mark all notifications as read
 */
export const markAllNotificationsRead = createAsyncThunk(
  "notifications/markAllRead",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ProjectServices.readAllNotifications();
      return response.data;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to mark all notifications as read";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Approve a notification (decision category)
 */
export const approveNotification = createAsyncThunk(
  "notifications/approve",
  async ({ notificationId, comments = "" }, { rejectWithValue }) => {
    try {
      const response = await ProjectServices.approveNotification(
        notificationId,
        { comments },
      );
      return { id: notificationId, ...response.data };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to approve notification";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Reject a notification (decision category)
 */
export const rejectNotification = createAsyncThunk(
  "notifications/reject",
  async ({ notificationId, reason }, { rejectWithValue }) => {
    try {
      const response = await ProjectServices.rejectNotification(
        notificationId,
        { reason },
      );
      return { id: notificationId, ...response.data };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to reject notification";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Delegate a notification to another user
 */
export const delegateNotification = createAsyncThunk(
  "notifications/delegate",
  async ({ notificationId, delegateTo, reason = "" }, { rejectWithValue }) => {
    try {
      const response = await ProjectServices.delegateNotification(
        notificationId,
        {
          delegate_to: delegateTo,
          reason,
        },
      );
      return { id: notificationId, ...response.data };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delegate notification";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Escalate a notification
 */
export const escalateNotification = createAsyncThunk(
  "notifications/escalate",
  async (
    { notificationId, escalationType, details, escalateTo },
    { rejectWithValue },
  ) => {
    try {
      const response = await ProjectServices.escalateNotification(
        notificationId,
        {
          escalation_type: escalationType,
          details,
          escalate_to: escalateTo,
        },
      );
      return { id: notificationId, ...response.data };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to escalate notification";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Delete a single notification
 */
export const deleteNotification = createAsyncThunk(
  "notifications/delete",
  async (notificationId, { rejectWithValue }) => {
    try {
      await ProjectServices.deleteNotification(notificationId);
      return { id: notificationId };
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete notification";
      return rejectWithValue(errorMsg);
    }
  },
);

/**
 * Delete all unread notifications
 */
export const deleteAllUnreadNotifications = createAsyncThunk(
  "notifications/deleteAllUnread",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ProjectServices.deleteAllUnread();
      return response.data;
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete all unread notifications";
      return rejectWithValue(errorMsg);
    }
  },
);
